import axios from 'axios';
import { getAccessToken, getRefreshToken, setAccessToken, setRefreshToken, clearTokens } from './authTokens';
// Both of these ultimately import back into this file (store -> authApi ->
// authBaseQuery -> authAxios), which is fine: neither binding is touched
// until inside the response interceptor callback below, long after every
// module involved has finished its own top-level evaluation. Needed here
// because the mustChangePassword gate has to be caught for ANY protected
// call, not just the ones a React component happens to be watching.
import { store } from './store';
import { forceMustChangePassword } from '../features/auth/authSlice';

// Real Cavan IMS backend — the only part of the API that's live right now
// (see docs: investors/portfolios/transactions/reports are "not yet
// populated"). Every other feature slice in this app still talks to the
// in-memory mock (see axiosBaseQuery.js / DEMO_MODE) until those resource
// areas ship on the real API too.
const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || 'https://cavan-investment-app-backend.onrender.com';
export const AUTH_API_BASE = `${API_ORIGIN}/api/v1`;

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export const authAxios = axios.create({
  baseURL: AUTH_API_BASE,
  // Lets the browser hold/send refresh_token + csrf_token cookies if the
  // deployment ever puts frontend and backend on a shared registrable
  // domain. Harmless no-op otherwise — see authTokens.js for why we don't
  // depend on it.
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

authAxios.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Double-submit CSRF header, required only on cookie-authenticated
  // requests. Best-effort: this cookie is only readable by our JS when
  // frontend and API share an origin/domain, which attaching it or not
  // doesn't change for the body-token path we rely on (see authTokens.js).
  const method = (config.method || 'get').toUpperCase();
  if (method !== 'GET') {
    const csrf = readCookie('csrf_token');
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }

  return config;
});

let refreshPromise = null;

/**
 * Calls POST /auth/refresh, always sending whatever refresh token we have
 * explicitly in the body (see authTokens.js for why). Coalesces concurrent
 * callers into a single in-flight request so a burst of 401s from several
 * parallel requests only triggers one refresh.
 */
export function refreshSession() {
  if (refreshPromise) return refreshPromise;

  const token = getRefreshToken();
  refreshPromise = axios
    .post(
      `${AUTH_API_BASE}/auth/refresh`,
      token ? { refresh_token: token } : {},
      { withCredentials: true, headers: { Accept: 'application/json' } }
    )
    .then((res) => {
      const { access_token, refresh_token } = res.data ?? {};
      if (access_token) setAccessToken(access_token);
      if (refresh_token) setRefreshToken(refresh_token);
      return access_token ?? null;
    })
    .catch((err) => {
      clearTokens();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

const AUTH_ENDPOINTS_EXEMPT_FROM_RETRY = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/login/verify-2fa', '/auth/magic-link'];

// A 401 on any protected call gets exactly one silent refresh-and-retry —
// this is what keeps a short-lived access token from ever surfacing as a
// login prompt mid-session. Auth endpoints themselves are exempt: a failed
// login/refresh means "wrong credentials"/"session over", not "retry me".
authAxios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const isExempt = AUTH_ENDPOINTS_EXEMPT_FROM_RETRY.some((path) => original?.url?.includes(path));

    // must_change_password blocks every protected endpoint except
    // /auth/change-password and /auth/logout (see docs). Catching it here,
    // globally, means a page doesn't need its own per-call check for this —
    // ProtectedRoute already reads the same flag on every render, so
    // flipping it in the store is enough to redirect immediately, no
    // matter which screen or API call triggered it.
    if (status === 403 && error.response?.data?.mustChangePassword && !original?.url?.includes('/auth/change-password')) {
      store.dispatch(forceMustChangePassword());
    }

    if (status === 401 && original && !original._retried && !isExempt) {
      original._retried = true;
      try {
        const newToken = await refreshSession();
        if (newToken) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newToken}`;
          return authAxios(original);
        }
      } catch {
        // fall through to reject below — caller (ProtectedRoute /
        // authTokenMiddleware) treats this as "session over".
      }
    }

    return Promise.reject(error);
  }
);

export default authAxios;
